// app/javascript/controllers/navbar_sidebar_controller.js
//
// Controls the collapsible left navigation sidebar in the marina admin interface.
// Handles expand/collapse toggle, flyout submenu display in collapsed state, and
// state persistence across sessions. Uses el-transition for smooth animations.
//
import { Controller } from "@hotwired/stimulus";
import { enter, leave } from "el-transition";

// Constants to avoid magic strings
const FLYOUT_Z_INDEX = 50;
const FLYOUT_ENTER_CLASSES = ["opacity-0", "scale-95", "translate-x-1"];
const FLYOUT_BASE_CLASSES = "bg-white rounded-lg shadow-lg border py-2 min-w-48 transform transition-all duration-200 ease-out";

export default class extends Controller {
  static targets = ["sidebar", "toggle", "section", "flyout", "mainContent", "toggleIcon"];

  static values = {
    collapsed: Boolean,
    sections: Object,
  };

  connect() {
    // Initialize from localStorage or default to expanded
    this.collapsedValue = localStorage.getItem("sidebarCollapsed") === "true";
    this.isAnimating = false; // Debounce flag for rapid toggles
    this.updateSidebarState();
    this.setupEventListeners();
  }

  disconnect() {
    this.removeEventListeners();
  }

  setupEventListeners() {
    // Close flyouts when clicking outside
    this.clickOutsideHandler = this.handleClickOutside.bind(this);
    document.addEventListener("click", this.clickOutsideHandler);

    // Handle escape key to close flyouts
    this.escapeHandler = this.handleEscape.bind(this);
    document.addEventListener("keydown", this.escapeHandler);
  }

  removeEventListeners() {
    document.removeEventListener("click", this.clickOutsideHandler);
    document.removeEventListener("keydown", this.escapeHandler);
  }

  // Check if the controller is running in the mobile menu context
  isMobileContext() {
    return this.element.querySelector('[data-mobile-context="true"]') !== null;
  }

  // Toggle sidebar collapsed/expanded state
  toggle() {
    if (this.isAnimating) return; // Debounce rapid toggles

    // Don't toggle in mobile context
    if (this.isMobileContext()) return;

    this.isAnimating = true;
    this.collapsedValue = !this.collapsedValue;
    localStorage.setItem("sidebarCollapsed", this.collapsedValue.toString());
    this.updateSidebarState();
    this.closeAllFlyouts();

    // Reset animation flag after transition
    setTimeout(() => { this.isAnimating = false; }, 200);
  }

  // Handle section clicks in collapsed mode
  showSection(event) {
    event.preventDefault();
    event.stopPropagation();

    const sectionElement = event.currentTarget;
    const { sectionKey } = sectionElement.dataset;

    if (this.collapsedValue) {
      // In collapsed mode, show flyout
      this.showFlyout(sectionElement, sectionKey);
    } else {
      // In expanded mode, toggle section as before
      this.toggleSection(sectionElement, sectionKey);
    }
  }

  // Show flyout menu for collapsed sidebar
  showFlyout(sectionElement, sectionKey) {
    if (this.isAnimating) return; // Debounce rapid interactions

    this.closeAllFlyouts();

    const flyout = this.createFlyout(sectionKey);
    if (!flyout) return; // Guard against missing sections

    const rect = sectionElement.getBoundingClientRect();

    // Position flyout to the right of the icon
    flyout.style.position = "fixed";
    flyout.style.top = `${rect.top}px`;
    flyout.style.left = `${rect.right + 8}px`;
    flyout.style.zIndex = FLYOUT_Z_INDEX;

    // Set initial state for animation
    flyout.classList.add(...FLYOUT_ENTER_CLASSES);
    flyout.setAttribute("role", "menu");
    flyout.setAttribute("aria-label", `${sectionKey} submenu`);

    document.body.appendChild(flyout);

    // Use el-transition enter animation
    this.isAnimating = true;
    enter(flyout)
      .then(() => {
        flyout.classList.remove("opacity-0", "scale-95", "translate-x-1");
        this.isAnimating = false;
        // Focus first link for keyboard accessibility
        const firstLink = flyout.querySelector("a");
        if (firstLink) firstLink.focus();
      })
      .catch(() => { this.isAnimating = false; });
  }

  // Create flyout menu dynamically
  createFlyout(sectionKey) {
    const section = this.sectionsValue[sectionKey];
    if (!section || !Array.isArray(section.items)) return null;

    const flyout = document.createElement("div");
    flyout.className = FLYOUT_BASE_CLASSES;
    flyout.dataset.flyout = sectionKey;

    // Add section items
    section.items.forEach((item) => {
      const link = document.createElement("a");
      link.href = item.path;
      link.className = "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900";
      link.textContent = item.label;
      link.setAttribute("role", "menuitem");

      // Add notification badges if present
      if (item.badge) {
        const badge = document.createElement("span");
        badge.className = "ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800";
        badge.textContent = item.badge;
        link.appendChild(badge);
      }

      flyout.appendChild(link);
    });

    return flyout;
  }

  // Toggle section in expanded mode (preserve existing behavior)
  /* eslint-disable class-methods-use-this */
  toggleSection(sectionElement, sectionKey) {
    const isOpen = localStorage.getItem(`${sectionKey}Open`) === "true";
    const newState = !isOpen;

    localStorage.setItem(`${sectionKey}Open`, newState.toString());

    // Update ARIA attributes
    sectionElement.setAttribute("aria-expanded", newState.toString());

    // Find the submenu and toggle it
    const submenu = sectionElement.closest("section").querySelector(`[data-section-submenu="${sectionKey}"]`);
    if (submenu) {
      if (newState) {
        submenu.style.display = "block";
        submenu.classList.remove("hidden");
      } else {
        submenu.style.display = "none";
        submenu.classList.add("hidden");
      }

      // Update arrow rotation
      const arrow = sectionElement.closest("section").querySelector(".section-arrow");
      if (arrow) {
        arrow.classList.toggle("rotate-90", newState);
      }
    }
  }
  /* eslint-enable class-methods-use-this */

  // Close all open flyouts
  /* eslint-disable class-methods-use-this */
  closeAllFlyouts() {
    const flyouts = document.querySelectorAll("[data-flyout]");
    if (flyouts.length === 0) return;

    // Use el-transition leave animation (similar to side_panel_controller)
    const promises = Array.from(flyouts).map((flyout) => leave(flyout));

    Promise.all(promises).then(() => {
      flyouts.forEach((flyout) => {
        if (flyout.parentNode) {
          flyout.parentNode.removeChild(flyout);
        }
      });
    });
  }
  /* eslint-enable class-methods-use-this */

  // Handle clicks outside flyouts
  handleClickOutside(event) {
    const flyouts = document.querySelectorAll("[data-flyout]");
    if (flyouts.length === 0) return;

    let clickedInside = false;
    flyouts.forEach((flyout) => {
      if (flyout.contains(event.target)) {
        clickedInside = true;
      }
    });

    if (!clickedInside && !this.sidebarTarget.contains(event.target)) {
      this.closeAllFlyouts();
    }
  }

  // Handle escape key
  handleEscape(event) {
    if (event.key === "Escape") {
      this.closeAllFlyouts();
    }
  }

  // Update sidebar visual state
  updateSidebarState() {
    const isMobileContext = this.isMobileContext();

    if (this.collapsedValue) {
      // Collapsed state
      if (!isMobileContext) {
        this.sidebarTarget.classList.remove("w-52");
        this.sidebarTarget.classList.add("w-12");
      }

      // Hide all text content
      this.sidebarTarget.querySelectorAll(".sidebar-text").forEach((el) => {
        el.classList.add("hidden");
      });

      // Show collapsed toggle icon
      this.updateToggleIcon(true);

      // Close any open sections
      this.closeAllSections();
    } else {
      // Expanded state
      if (!isMobileContext) {
        this.sidebarTarget.classList.remove("w-12");
        this.sidebarTarget.classList.add("w-52");
      }

      // Show all text content
      this.sidebarTarget.querySelectorAll(".sidebar-text").forEach((el) => {
        el.classList.remove("hidden");
      });

      // Show expanded toggle icon
      this.updateToggleIcon(false);

      // Restore section states from localStorage
      this.restoreSectionStates();
    }

    this.closeAllFlyouts();
  }

  // Update toggle icon
  updateToggleIcon(collapsed) {
    if (!this.hasToggleTarget) return;
    const leftChevron = this.toggleTarget.querySelector(".chevron-left");
    const rightChevron = this.toggleTarget.querySelector(".chevron-right");

    if (collapsed) {
      leftChevron?.classList.add("hidden");
      rightChevron?.classList.remove("hidden");
    } else {
      leftChevron?.classList.remove("hidden");
      rightChevron?.classList.add("hidden");
    }
  }

  // Close all expanded sections
  closeAllSections() {
    this.sidebarTarget.querySelectorAll("[data-section-submenu]").forEach((submenu) => {
      const element = submenu;
      element.style.display = "none";
      element.classList.add("hidden");
    });

    this.sidebarTarget.querySelectorAll(".section-arrow").forEach((arrow) => {
      arrow.classList.remove("rotate-90");
    });
  }

  // Restore section states from localStorage
  restoreSectionStates() {
    Object.keys(this.sectionsValue).forEach((sectionKey) => {
      const isOpen = localStorage.getItem(`${sectionKey}Open`) === "true";
      const sectionButton = this.sidebarTarget.querySelector(`[data-section-key="${sectionKey}"]`);

      if (sectionButton) {
        // Set ARIA attribute
        sectionButton.setAttribute("aria-expanded", isOpen.toString());

        if (isOpen) {
          const submenu = sectionButton.closest("section").querySelector(`[data-section-submenu="${sectionKey}"]`);
          if (submenu) {
            submenu.style.display = "block";
            submenu.classList.remove("hidden");
            const arrow = sectionButton.closest("section").querySelector(".section-arrow");
            if (arrow) {
              arrow.classList.add("rotate-90");
            }
          }
        }
      }
    });
  }

  // Getter for collapsed state changes
  collapsedValueChanged() {
    if (this.hasMainContentTarget) {
      // Adjust main content width when sidebar state changes
      if (this.collapsedValue) {
        this.mainContentTarget.classList.add("sidebar-collapsed");
      } else {
        this.mainContentTarget.classList.remove("sidebar-collapsed");
      }
    }
  }
}
